<template>
  <section>
    <one-line-form :submit="updateEmail" :saving="isSaving ? email : ''" :submittable="isSubmittable" :button="!showWarning && !emailCorrect" :loading="isSaving && (waitForCallback || isLoading)">
      <template slot="underlay">
        <transition name="fade">
          <div class="input">
            <ins v-if="showWarning" :style="{fontSize : warningFontSize}">
              {{ email }}
              <span>
                {{ i18n.t('base:double_check') }}
              </span>
            </ins>
          </div>
        </transition>
      </template>
      <input-field name="email"
                   :type="'email'"
                   :title="i18n.t('email:email')"
                   :value="email"
                   :hideLabel="showWarning"
                   :autocomplete="'email'"
                   :placeholder="i18n.t('email:placeholder')"
                   @input="syncEmail"
                   v-keyboard="'email'"
                   ref="input"
                   @tooltip-clicked="onTooltipClick"
                   :tooltip="getTooltipContent"
                   :class="{ warning: showWarning }"
                   :underline="suggestionsEnabled && email === ''" />
    </one-line-form>

    <div v-show="suggestionsEnabled && showSuggestions" class="suggestions" :class="style.suggestions">
      <div class="list">
        <span v-for="(s, index) in suggestions" :class="{'active' : activeSuggestionIndex === index }"  @click="selectSuggestionProvider(s)" :key="`s-${index}`">{{ s }}</span>
      </div>
    </div>

    <helper v-if="suggestionsEnabled && !showWarning && !(isEmail && isValid) && !showSuggestions"> {{ i18n.t('email:example', { email: 'john@gmail.com' }) || 'Example: john@example.com' }} </helper>

    <div id="consent-hints" v-if="!this.screenParams.validation">
      <helper v-if="GDPR.showSoftHint" v-show="isEmail && !showWarning && !isSaving && isValid">{{ i18n.t('GDPR:soft_hint') }} <a href="#" @click.prevent="togglePrivacy">{{ i18n.t('GDPR:soft_hint_link') }}</a></helper>

      <helper v-if="GDPR.showSupersoftHint" v-show="isEmail && !showWarning && !isSaving">{{ i18n.t('GDPR:accept_privacy') }} <a href="#" @click.prevent="updateEmail(false)">{{ i18n.t('GDPR:accept_privacy_link') }}</a></helper>

      <helper class="register-manually" v-if="GDPR.showManualFlowHint" v-show="isEmail && !showWarning && !isSaving">{{ i18n.t('GDPR:register_manually') }} <a href="#" @click.prevent="registerManually">{{ i18n.t('GDPR:register_manually_link') }}</a></helper>
    </div>

    <social-login v-if="partner.socialMedia.enabled && !showWarning && !email"></social-login>

    <transition name="bottom" appear>
      <action-bar :hint="i18n.t('email:ensure_correct')" v-if="showWarning && showActions">
        <!--<button @click="editEmail" class="primary">Bearbeiten</button>
        <button @click="approveEmail">Sie ist korrekt</button>-->
        <ul class="columns">
          <li @click="editEmail" class="primary">
            <span>{{ i18n.t('base:edit') }}</span>
          </li>
           <li @click="approveEmail" v-if="allowIncorrectEmail">
            <span>{{ i18n.t('base:correct') }}</span>
          </li>
        </ul>
      </action-bar>
    </transition>
    <!--showWarning: {{ showWarning }}<br />-->
    <!--isEmail: {{ isEmail }}<br />-->
    <!--isDeliverable: {{ isDeliverable }}-->
    <modal :title="i18n.t(`softGDPR:personal.link_title`)" v-if="privacyShown" @closeModal="togglePrivacy" :content="i18n.t('softGDPR:personal:link_content')" />
    <show-keyboard v-if="!showWarning"></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  ul {
    @include button-list;
  }


  ins {
    color: transparent;
    background: #FFE676;
    display: inline-block;
    position: relative;
    max-width: 100%;

    span {
      background: no-repeat left 9px;
      background-image: url('~static/warning_pointer.svg');
      position: absolute;
      display: block;
      left: 40px;
      bottom: 100%;
      height: 40px;
      color: $inputWarningColor;
      color: $inputWarningColor-portal;
      font-size: 1rem;
      font-weight: lighter;
      padding-left: 42px;
      margin-bottom: 10px;
      line-height: 120%;
    }
  }

  #consent-hints {
    @include tablet {
      width: $tablet-content-width;
    }
    @include desktop {
      width: 100%;
    }

    @include desktop-big {
      .register-manually {
        margin-top: 25px;
      }
    }
  }

  div.suggestions {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    width: 100%;
    max-width: 315px;
    padding: 10px 0;
    -webkit-overflow-scrolling: touch;
    position: relative;
    @include tablet {
      max-width: $tablet-content-width;
    }
    @include desktop-big {
      max-width: 380px;
    }
    &::-webkit-scrollbar {
      display: none;
    }
    &.left {
      &:before {
        content: '';
        width: 40px;
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        background: linear-gradient(to right, rgba(255,255,255,1) 20%, rgba(255,255,255,0));
      }
    }
    &.right {
      &:after {
        content: '';
        width: 40px;
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        background: linear-gradient(to left, rgba(255,255,255,1) 20%, rgba(255,255,255,0));
      }
    }
    .list {
      width: 100%;
      display: flex;
      overflow-x: auto;
      padding: 0;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    span {
      display: inline-block;
      background-color: #F4F4F4;
      color: #84929C;
      font-size: 16px;
      line-height: 20px;
      border-radius: 5px;
      flex: 0 0 auto;
      margin-right: 5px;
      padding: 8px;
      cursor: pointer;
      &:hover, &:active {
        background-color: $actionNormalColor;
        color: $suggestionHoverColor;
      }
      &.active {
        background-color: $actionNormalColor;
        color: white;
      }
    }
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'
  import { setSelection } from '../utils/selection'
  import { isValid as isValidEmail } from '../utils/email'

  import BottomHints from '../components/BottomHints'
  import Modal from '../components/Modal'
  import AdditionalKeyboard from '../components/AdditionalKeyboard'
  import SocialLogin from '../components/SocialLogin'
  import Mousetrap from 'mousetrap'

  export default Vue.component('credentials-email-screen', {
    
    components: {
      'bottom-hints': BottomHints,
      'modal': Modal,
      'additional-keyboard': AdditionalKeyboard,
      'social-login': SocialLogin
    },
    props: ['screenParams'],
    data () {
      const GDPR = this.$store.state.GDPR
      const credentials = this.$store.state.credentials
      const autofill = this.$store.state.autofill

      const facebookData = this.$store.state.socialMediaData.facebook && this.$store.state.socialMediaData.facebook.profile
      const googleData = this.$store.state.socialMediaData.google && this.$store.state.socialMediaData.google.profile
      const linkedInData = this.$store.state.socialMediaData.linkedIn && this.$store.state.socialMediaData.linkedIn.profile
      const socialMediaData = facebookData || googleData || linkedInData

      return {
        GDPR: GDPR,
        emailOld: '',
        email: credentials.email || (autofill.enabled && autofill.email) || (socialMediaData && socialMediaData.email) || '',
        isSaving: false,
        showWarning: false,
        fontSize: 22,
        showActions: false,
        privacyShown: false,
        dataCheckCount: 0,
        emailCorrect: false,
        isLoading: true,
        validationTimedOut: false,
        firstRun: true,
        showSuggestions: false,
        warningFontSize: null,
        activeSuggestionIndex: -1
      }
    },
    computed: {
      ...mapState(['credentials', 'user', 'emailValidation', 'app', 'dataStatus', 'partner', 'socialMediaData', 'customData']),
      style() {
        return {
          suggestions: this.$css(`
            span {
              &:hover, &:active {
                background-color: ${this.theme.actionNormalColor} !important;
                color: ${this.theme.suggestionHoverColor} !important;
              }
              &.active {
                background-color: ${this.theme.actionNormalColor} !important;
              }
            }
          `)
        }
      },
      isEmail () {
        const isEmail = !!this.email.trim() && isValidEmail(this.email)
        if (!this.screenParams.validation && (this.partner.enableEmailSuggestion || (this.customData && this.customData.enableEmailSuggestion))) {
          this.getEmailSuggestion(isEmail)
        }
        return isEmail
      },

      isValid () {
        
        const isOldEmail = (this.screenParams.validation || this.app.fromErrorScreen) && (this.email === this.emailOld)
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$/
        const firstPartEmailReg = /[a-zA-Z0-9]{1,}/
        return !isOldEmail && emailRegex.test(this.email) && firstPartEmailReg.test(this.email.split('@')[0])
      },

      waitForCallback () {
        const waitForCallbackAvailable = this.partner.waitForCallback && this.partner.waitForCallback.length > 0
        return waitForCallbackAvailable && this.partner.waitForCallback.includes('email')
      },

      allowIncorrectEmail () {
        return this.partner.allowIncorrectEmail === undefined ? true : this.partner.allowIncorrectEmail
      },

      getTooltipContent () {
        const shouldGetTooltip = this.isEmail && !this.screenParams.validation && this.emailValidation.did_you_mean
        if (shouldGetTooltip) {
          return this.i18n.t('email:did_you_mean', { email: this.emailValidation.did_you_mean })
        }
        return null
      },

      suggestionsEnabled () {
        return this.partner && this.partner.suggestionsProvider && this.partner.suggestionsProvider.enabled
      },

      suggestions () {
        if (this.suggestionsEnabled) {
          const list = this.partner.suggestionsProvider.providers[this.app.country.code] || this.partner.suggestionsProvider.providers.default
          return list
        }
        return {}
      },
      isSubmittable () {
        return (this.isEmail && this.isValid) || this.validationTimedOut
      },
      waitForRemoteHandshake () {
        if (this.app.isFromAffiliate && this.app.remoteLoading) {
          this.$watch('app.remoteLoading', (newValue) => {
            if (newValue === false) {
              this.updateEmail()
            }
          })
          return true
        }

        return false
      }
    },
    watch: {
      showWarning () {
        const input = this.$el && this.$el.querySelector('input')
        if (input) {
          this.warningFontSize = input.style.fontSize
        }

        if (this.showWarning && document.activeElement.nodeName.toLowerCase() === 'input') {
          document.activeElement.blur()
        }

        if (this.showWarning) {
          this.$emit('tooltip', ['horizontal', 'enter'])
          return this.$emit('errorScreen', true)
        } else {
          this.refreshToolTip()
        }
        this.$emit('errorScreen', false)
      },

      'socialMediaData' () {
        const facebookData = this.$store.state.socialMediaData.facebook && this.$store.state.socialMediaData.facebook.profile
        const googleData = this.$store.state.socialMediaData.google && this.$store.state.socialMediaData.google.profile
        const linkedInData = this.$store.state.socialMediaData.linkedIn && this.$store.state.socialMediaData.linkedIn.profile
        const socialMediaData = facebookData || googleData || linkedInData

        const socialMediaEmail = socialMediaData && socialMediaData.email

        if (!this.email && socialMediaEmail) {
          this.email = socialMediaEmail
        }
      },

      'emailValidation.isLoading' (val) { // when validation request is done only after the user has reached the email-validation screen
        if (!val && this.screenParams.validation && this.firstRun) {
          this.isLoading = false
          this.isSaving = false
          this.firstRun = false
          this.checkWarning()
        }
      },

      email (val) {
        this.showSuggestions = val && (val.substr(-1) === '@') && !this.showWarning
        if (this.showSuggestions) {
          setTimeout(() => {
            this.setSuggestionRectShadows()
          }, 500)
        }
      },
      privacyShown () {
        if (this.privacyShown) {
          this.$emit('tooltip', ['esc'])
        } else {
          this.refreshToolTip()
        }
      },

      isSubmittable () {
        this.refreshToolTip()
      },

      isSaving () {
        if (this.isSaving) {
          this.$emit('tooltip', 'hide')
        }
      }
    },
    created () {
      if (this.screenParams.validation && this.emailValidation.isLoading) { // if the user reaches the validation screen and the request is still loading
        this.isLoading = true
        setTimeout(() => {
          this.isSaving = true
        }, 10)
        this.checkValidationTimeout(2000)
      } else {
        this.firstRun = false
      }

      this.checkWarning()
      this.emailOld = this.email
    },
    mounted () {
      setTimeout(() => {
        this.showActions = true
      }, 500)

      setTimeout(this.catchEdit, 0)

      const list = document.querySelector('.suggestions>div')
      if (list) {
        list.addEventListener('scroll', this.setSuggestionRectShadows)
        window.addEventListener('resize', this.setSuggestionRectShadows)
        this.setSuggestionRectShadows()
      }

      if (this.suggestionsEnabled) {
        this.manipulateSuggestions()
      }
    },
    destroyed () {
      Mousetrap.unbind(['left', 'up', 'down', 'right', 'enter'])
    },
    methods: {
      manipulateSuggestions () {
         const input = document.querySelector('.input-wrapper input[name=email]')
         if (input) input.classList.add('mousetrap')
  
          Mousetrap.bind(['down'], () => {
            this.activeSuggestionIndex = 0
            document.activeElement.blur()
          })
  
          Mousetrap.bind(['up'], () => {
            this.activeSuggestionIndex = -1
            if (!this.isEmailInputActive()) {
              input.focus()
            }
          })

          Mousetrap.bind(['right'], () => {
            if (!this.isEmailInputActive() && this.activeSuggestionIndex + 1 < this.suggestions.length) {
                this.activeSuggestionIndex++
            }
          })
  
          Mousetrap.bind(['left'], () => {
            if (!this.isEmailInputActive() && this.activeSuggestionIndex > 0) {
                this.activeSuggestionIndex--
            }
         })

          Mousetrap.bind(['enter'], () => {
            if (this.activeSuggestionIndex >= 0 && !this.isEmailInputActive() && this.showSuggestions) {
                this.selectSuggestionProvider(this.suggestions[this.activeSuggestionIndex])
            }
         })
      },
      isEmailInputActive () {
        return document.activeElement.nodeName.toLowerCase() === 'input'
      },
      getEmailSuggestion (email) {
        if (email) {
          setTimeout(() => {
            if (!this.emailValidation.isLoading) {
              this.$store.dispatch('validateEmail', {
                email: this.email
              })
            }
          }, 110)
        } else if (this.emailValidation.did_you_mean) {
          this.$store.commit('updateEmailValidation', {
            did_you_mean: null
          })
        }
      },
      onTooltipClick (event) {
        if (event.target && event.target.tagName === 'SPAN' && this.emailValidation.did_you_mean) {
          this.email = this.emailValidation.did_you_mean
          this.$store.commit('updateEmailValidation', {
            did_you_mean: null
          })
        }
      },
      syncEmail (value) {
        this.email = value
      },

      checkWarning () {
        if ((!this.isEmail && this.screenParams.validation) || (this.isEmail && this.emailValidation.isDeliverable === false && !this.emailValidation.isApproved && this.screenParams.validation)) {
          this.showWarning = true
        } else if (this.screenParams.validation && this.emailValidation.isDeliverable) {
          this.isSaving = true
          this.$store.dispatch('replace', this.screenParams.nextScreenRoute)
        }
        if (this.showWarning) {
          this.$emit('tooltip', ['horizontal', 'enter'])
        } else {
          this.refreshToolTip()
        }
      },

      checkValidationTimeout (duration) { // to skip the validation if the request takes longer than the duration specified
        setTimeout(() => {
          if (this.emailValidation.isLoading) {
            this.$store.dispatch('updateEmailValidation', {
              isApproved: true,
              isDeliverable: true,
              isLoading: false
            })
            this.isLoading = false
            this.validationTimedOut = true
            this.$store.dispatch('replace', this.screenParams.nextScreenRoute)
          }
        }, duration)
      },

      approveEmail () {
        if (!this.allowIncorrectEmail) return
        this.showWarning = false
        this.emailCorrect = true
        const input = this.$el.querySelector('input')

        const focusInput = !!this.screenParams.focusInput || this.screenParams.focusInput === undefined

        if (input && focusInput) {
          setSelection(input, { start: 0, end: 0 })
        }
        this.$store.dispatch('updateEmailValidation', {
          isApproved: true,
          isDeliverable: true
        })

        this.updateEmail()

        this.$store.dispatch('updateEmailValidation', {
          isDeliverable: null
        })
      },

      async updateEmail (accepted = true) {
        this.isSaving = accepted

        if (this.waitForRemoteHandshake) {
          this.isLoading = true
          return false
        }

        const emailError = this.errorPresent()
        const isErrorEmail = emailError && emailError.diff.credentials.email === this.email
        const isOldEmail = this.emailOld === this.email
        const isOldErrorEmail = isErrorEmail && isOldEmail
        const isValidatedEmail = this.emailValidation.email && this.emailValidation.email === this.email

        const canBatchAcceptGDPR = this.GDPR.showSupersoftHint
        if (canBatchAcceptGDPR) {
          this.$store.dispatch('updateGDPR', {
            accepted: accepted
          })
        }

        const hasLookupHints = this.GDPR.showManualFlowHint || this.GDPR.showSoftHint
        if (hasLookupHints) {
          this.$store.commit('updatePartner', {
            isDataLookupEnabled: accepted
          })
        }

        if (!isOldEmail && emailError) {
          this.$store.dispatch('deleteError', {
            errorName: emailError.name
          })
        }

        if (!isValidatedEmail || (isValidatedEmail && !this.emailValidation.isDeliverable)) {
          if (this.screenParams.validation) { // on email-validation await for response (validation loop)
            this.isSaving = true
            this.isLoading = true
            this.checkValidationTimeout(5000)
            await this.$store.dispatch('validateEmail', {
              email: this.email
            })
            this.isLoading = false
            if (!this.emailValidation.isDeliverable) {
              this.showWarning = true
              this.isSaving = false
              this.emailOld = this.email
              return
            }
          } else { // on email screen, don't await for response just launch the request & proceed
            this.$store.dispatch('validateEmail', {
              email: this.email
            })
          }
        }

        this.$store.dispatch('updateApp', {
          fromErrorScreen: false
        })

        if (!isOldEmail) {
          this.$store.commit('updateDataStatus', {
            checking: true
          })
          return this.dataLeave()
        } else if (this.waitForCallback && isOldErrorEmail) {
          return this.$router.replace(`/error/${emailError.name}`)
        }

        this.trackPrefill('email', this.email)
        this.isLoading = false
        this.$store.dispatch(this.screenParams.validation ? 'replace' : 'next', this.screenParams.nextScreenRoute)
      },

      editEmail () {
        this.isSaving = false
        this.showWarning = false
        this.emailOld = this.email

        this.$store.dispatch('updateEmailValidation', {
          isApproved: null,
          isDeliverable: null
        })

        const input = this.$el.querySelector('input')

        if (input) {
          input.focus()
        }
      },

      catchEdit () {
        const input = this.$el.querySelector('input')

        input.addEventListener('focus', () => {
          if (this.showWarning) this.editEmail()
        }, true)

        input.addEventListener('click', () => {
          this.showWarning = false
        }, true)
      },

      togglePrivacy () {
        this.privacyShown = !this.privacyShown
      },
      errorPresent (errorSearchName) {
        const errors = this.dataStatus.errors && this.dataStatus.errors.length > 0 ? this.dataStatus.errors : false

        if (errors) {
          return errors.find((error) => {
            const credentialsError = error.diff && error.diff.credentials
            return credentialsError && credentialsError.hasOwnProperty('email')
          })
        }
      },
      dataLeave () {
        return setTimeout(() => {
          const checkingDone = !this.dataStatus.checking
          const emailError = this.errorPresent()

          if ((checkingDone && !emailError) || this.dataCheckCount === 10) {
            return this.$store.dispatch('next', this.screenParams.nextScreenRoute)
          } else if (checkingDone && emailError && emailError.name === 'duplicateAccount') {
            return this.$router.replace(`/error/${emailError.name}`)
          }

          this.dataCheckCount++
          return this.dataLeave()
        }, 500)
      },
      registerManually () {
        this.$store.dispatch('updatePartner', {
          isDataLookupEnabled: false
        })
        this.updateEmail(false)
      },

      selectSuggestionProvider (s) {
        this.email = `${this.email}${s}`

        this.$rmt.trackEvent({
          actor: 'user',
          category: s,
          action: 'select-email-provider'
        })
      },
      refreshToolTip () {
        if (!this.showWarning && !this.isSaving) {
          this.$emit('tooltip', this.isSubmittable ? ['enter'] : ['type'])
        }
      },
      setSuggestionRectShadows () {
        const container = document.querySelector('.suggestions')
        const list = document.querySelector('.suggestions>div')

        if (!list || !container) return

        if (list.scrollWidth > list.clientWidth) {
          container.classList.add('right')
        }
        if (list.scrollLeft > 0) {
          container.classList.add('left')
        } else {
          container.classList.remove('left')
        }
        if ((list.scrollLeft + list.clientWidth) === list.scrollWidth) {
          container.classList.remove('right')
        }
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/CredentialsEmail.vue